import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, MessageSquarePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, reportError } from "../lib/api";
import { Button, ErrorBox, Field, Select, Textarea } from "./kd";
import { MicButton, useKi } from "./Ki";

const pathParam = (re: RegExp) => window.location.pathname.match(re)?.[1];

export function TestBanner() {
  const loc = useLocation();
  const [on, setOn] = useState(false);
  const slug = loc.pathname.match(/^\/u\/([^/]+)/)?.[1];
  useEffect(() => {
    api.get("/public/testmodus", { params: slug ? { slug } : {} }).then((r) => setOn(r.data.testmodus)).catch(() => undefined);
  }, [slug]);
  useEffect(() => {
    const onErr = (e: ErrorEvent) => reportError(`${e.message} @${(e.filename || "").split("/").pop()}:${e.lineno}`);
    const onRej = (e: PromiseRejectionEvent) => { if (!e.reason?.isAxiosError) reportError(`Unhandled: ${e.reason?.message || e.reason}`); };
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => { window.removeEventListener("error", onErr); window.removeEventListener("unhandledrejection", onRej); };
  }, []);
  if (!on) return null;
  return (
    <div role="status" className="relative z-30 flex items-center justify-center gap-2 border-b border-warning bg-warning-bg px-4 py-2 text-center text-sm font-medium text-[#7A3E00]" data-testid="test-mode-banner">
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /><span className="sm:hidden">Testbetrieb – nur fiktive Daten</span><span className="hidden sm:inline">Testbetrieb – bitte nur fiktive Patientendaten eingeben</span>
    </div>
  );
}

function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const [kat, setKat] = useState("FEHLER");
  const [text, setText] = useState("");
  const [sprache, setSprache] = useState(false);
  const [shot, setShot] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const kctx = { slug: pathParam(/^\/u\/([^/]+)/), token: pathParam(/^\/f\/([^/]+)/) };
  const flags = useKi(kctx);
  const send = async () => {
    setBusy(true); setErr("");
    const fd = new FormData();
    Object.entries({ kategorie: kat, text, route: window.location.pathname, browser: navigator.userAgent, bildschirm: `${window.innerWidth}x${window.innerHeight}`,
      zeitpunkt: new Date().toISOString(), sprache: String(sprache) }).forEach(([k, v]) => fd.append(k, v));
    const slug = pathParam(/^\/u\/([^/]+)/); const token = pathParam(/^\/f\/([^/]+)/);
    if (slug) fd.append("slug", slug);
    if (token) fd.append("token", token);
    if (shot) fd.append("screenshot", shot);
    try { await api.post("/feedback", fd); toast.success("Danke für Ihre Rückmeldung!"); onClose(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Feedback" data-testid="feedback-dialog">
      <div className="kd-card w-full max-w-lg rounded-b-none p-5 sm:rounded-b-xl">
        <div className="mb-3 flex items-center justify-between"><h2>Feedback geben</h2>
          <button onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-bg" aria-label="Schließen" data-testid="feedback-close"><X className="h-5 w-5" aria-hidden /></button></div>
        <ErrorBox testid="feedback-error">{err}</ErrorBox>
        <Field label="Kategorie" htmlFor="fb-kat"><Select id="fb-kat" options={{ FEHLER: "Fehler", VERBESSERUNG: "Verbesserung", FRAGE: "Frage" }} value={kat} onChange={(e) => setKat(e.target.value)} data-testid="feedback-kategorie" /></Field>
        <Field label="Ihre Nachricht" required htmlFor="fb-text" hint="Bitte keine Patientendaten eingeben.">
          <Textarea id="fb-text" rows={4} value={text} onChange={(e) => setText(e.target.value)} data-testid="feedback-text" />
        </Field>
        {flags.sprache && <div className="mb-4"><MicButton ctx={kctx} onText={(t) => { setText((x) => (x ? `${x} ${t}` : t)); setSprache(true); }} testid="feedback-voice" /></div>}
        <Field label="Screenshot (optional, PNG/JPG)" htmlFor="fb-shot"><input id="fb-shot" type="file" accept="image/png,image/jpeg" onChange={(e) => setShot(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="feedback-screenshot" /></Field>
        <p className="mb-4 text-sm text-text-muted">Automatisch angehängt: Seite, Browser, Bildschirmgröße, Zeitpunkt.</p>
        <Button onClick={send} loading={busy} disabled={text.trim().length < 3} data-testid="feedback-submit">Absenden</Button>
      </div>
    </div>
  );
}

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const authPage = /^\/(login|registrieren|passwort)/.test(loc.pathname);
  return (
    <>
      <button onClick={() => setOpen(true)} className={`fixed bottom-4 right-4 z-40 ${authPage ? "hidden sm:flex" : "flex"} min-h-[44px] items-center gap-2 rounded-full bg-primary px-3 font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-primary-dark sm:left-4 sm:right-auto sm:px-4`} aria-label="Feedback geben" data-testid="feedback-button">
        <MessageSquarePlus className="h-5 w-5" aria-hidden /><span className="hidden sm:inline">Feedback</span>
      </button>
      {open && <FeedbackDialog onClose={() => setOpen(false)} />}
    </>
  );
}
